import React from 'react';

const StayCompliant = () => (
  <div className="container mx-auto px-4 py-8">
    <h1 className="text-3xl font-bold mb-6">Post-Quantum Cryptography and ISO/IEC 27001:2022 Compliance</h1>

    <div className="mb-8 border-b border-gray-200 py-6">
      <h2 className="text-xl font-semibold mb-2">1. Purpose and Scope</h2>
      <p className="mb-4">
        ISO/IEC 27001:2022 obliges organisations to apply "appropriate and effective use of cryptography" (Annex A 8.24) within a risk-based Information Security Management System (ISMS). Post-Quantum Cryptography (PQC) counters the emerging quantum threat to RSA, DSA and ECC, and therefore forms part of the control landscape that an auditor will expect to see when long-lived or high-value information assets are involved.
      </p>
    </div>

    <div className="mb-8 border-b border-gray-200 py-6">
      <h2 className="text-xl font-semibold mb-2">2. The Quantum Threat and SNDL Risk</h2>
      <ul className="list-disc pl-5 mb-4">
        <li className="mb-2"><strong>Quantum vulnerability:</strong> Shor's algorithm will enable sufficiently large quantum computers to break today's public-key primitives.</li>
        <li className="mb-2"><strong>SNDL scenario:</strong> Attackers can <strong>harvest encrypted traffic now and decrypt it later</strong> once quantum capabilities mature. ISO 27001 calls for controls that remain effective for the <em>entire confidentiality period</em> of the data; therefore SNDL is a present-day risk, not a distant worry.</li>
      </ul>
    </div>

    <div className="mb-8 border-b border-gray-200 py-6">
      <h2 className="text-xl font-semibold mb-2">3. ISO 27001 Controls Strengthened by PQC</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300 mb-4">
          <thead>
            <tr className="bg-gray-100">
              <th className="py-2 px-4 border-b text-left">ISO 27001:2022 Element</th>
              <th className="py-2 px-4 border-b text-left">PQC Contribution</th>
              <th className="py-2 px-4 border-b text-left">Compliance Outcome</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-2 px-4 border-b"><strong>Annex A 8.24 – Use of Cryptography</strong></td>
              <td className="py-2 px-4 border-b">Adds an algorithm-agility policy; approves PQC algorithms such as ML-KEM (FIPS 203); sets retirement dates for traditional RSA/ECC.</td>
              <td className="py-2 px-4 border-b">Demonstrates that cryptography stays "appropriate and effective" under evolving threats.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b"><strong>Annex A 8.25 – Key Management</strong></td>
              <td className="py-2 px-4 border-b">Tags keys with algorithm family, quantum-safe status and planned switch-over date; supports dual certificates (classical + PQC) during transition.</td>
              <td className="py-2 px-4 border-b">Shows full-lifecycle control and a documented migration path.</td>
            </tr>
            <tr>
              <td className="py-2 px-4 border-b"><strong>Clause 6 – Risk Assessment & Treatment</strong></td>
              <td className="py-2 px-4 border-b">Adds "quantum attack" and SNDL scenarios to the ISO 27005 risk register; links risk level to data shelf-life.</td>
              <td className="py-2 px-4 border-b">Auditors see the threat formally identified, evaluated and mitigated.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div className="mb-8 border-b border-gray-200 py-6">
      <h2 className="text-xl font-semibold mb-2">4. Practical Migration Steps</h2>
      <ol className="list-decimal pl-5 mb-4">
        <li className="mb-2"><strong>Cryptographic & Asset Inventory</strong> – Catalogue every use of RSA/ECC in TLS, SSH, VPNs, code-signing and PKI.</li>
        <li className="mb-2"><strong>SNDL Impact Assessment</strong> – Identify data whose confidentiality horizon exceeds five to ten years; mark it "SNDL-sensitive."</li>
        <li className="mb-2"><strong>Policy & Contract Update</strong> – Amend cryptographic policy, supplier requirements and SLAs to mandate algorithm agility and PQC adoption.</li>
        <li className="mb-2"><strong>Pilot Hybrid Deployments</strong> – Start with hybrid key exchange (X25519 + ML-KEM) and dual signatures (ECDSA + ML-DSA) to maintain backward compatibility while introducing quantum resistance.</li>
        <li className="mb-2"><strong>Training & Awareness</strong> – Brief developers, administrators and third parties on new standards, libraries and certificate profiles.</li>
        <li className="mb-2"><strong>Continual Improvement</strong> – Track NIST, IETF and national-scheme updates; review risk scores and decommission schedules at least annually.</li>
      </ol>
    </div>

    <div className="mb-8 border-b border-gray-200 py-6">
      <h2 className="text-xl font-semibold mb-2">5. Evidence for Auditors</h2>
      <ul className="list-disc pl-5 mb-4">
        <li className="mb-2"><strong>Statement of Applicability (SoA):</strong> Cross-reference SNDL and quantum threats to Annex A 8.24 and 8.25.</li>
        <li className="mb-2"><strong>Risk Register Entries:</strong> Include quantitative or semi-quantitative scores for quantum likelihood and impact.</li>
        <li className="mb-2"><strong>Change Records:</strong> Show policy revisions, key-rotation logs and test results for hybrid deployments.</li>
        <li className="mb-2"><strong>Management-Review Minutes:</strong> Document decisions on PQC timelines, budget and supplier alignment.</li>
      </ul>
    </div>

    <div className="mb-8 border-b border-gray-200 py-6">
      <h2 className="text-xl font-semibold mb-2">6. Benefits Beyond Compliance</h2>
      <ul className="list-disc pl-5 mb-4">
        <li className="mb-2"><strong>Long-Term Confidentiality:</strong> Eliminates the SNDL window for high-value data.</li>
        <li className="mb-2"><strong>Regulatory Alignment:</strong> Supports GDPR, NIS 2, PCI DSS v4 and sectoral schemes that hinge on ISO 27001 controls.</li>
        <li className="mb-2"><strong>Cost Efficiency:</strong> Synchronises PQC rollout with routine certificate renewals and hardware refresh cycles.</li>
        <li className="mb-2"><strong>Competitive Advantage:</strong> Early adoption signals diligence to customers and partners in defence, finance and healthcare.</li>
      </ul>
    </div>

    <div className="mt-10 bg-gray-100 p-6 rounded-lg">
      <h2 className="text-2xl font-bold mb-4">7. Key Takeaways</h2>
      <ol className="list-decimal pl-5 mb-0">
        <li className="mb-2">ISO 27001 focuses on <em>effectiveness</em> of cryptography across the data life-cycle; quantum computing shortens that window for classical algorithms.</li>
        <li className="mb-2">Documenting PQC adoption and SNDL mitigation within the ISMS provides concrete, audit-ready evidence that Annex A 8.24/8.25 and Clause 6 requirements are met.</li>
        <li className="mb-2">A phased, hybrid migration strategy balances operational continuity today with quantum resistance for tomorrow—fulfilling both security and compliance objectives.</li>
      </ol>
    </div>
  </div>
);

export default StayCompliant;
